import { useCallback, useMemo } from 'react';

import { useShowCauseW_Selector } from '@givebacks/api/core';
import {
  type ConsumerCheckoutDiscount,
  useAddDiscountToCheckout,
  useRemoveDiscountFromCheckout,
  useShowConsumerCheckout,
} from '@givebacks/api/store';
import { getErrorMessage_v2 } from 'core/Utils';
import { ActionIcon, Badge, Button, Group, Stack, Text, TextInput, Title, notifications } from 'ui';
import { useField } from 'ui/Form';
import { IconXTabler } from 'ui/Icons';

const DiscountBadge = ({
  discountCartItem,
  cartIds,
  causeId,
  checkoutId,
}: {
  discountCartItem: ConsumerCheckoutDiscount;
  cartIds: string[];
  causeId?: string;
  checkoutId: string;
}) => {
  const { mutate: removeDiscount } = useRemoveDiscountFromCheckout();
  const { data: causeData } = useShowCauseW_Selector({ uuid: causeId });

  const handleRemoveDiscount = useCallback(() => {
    removeDiscount(
      { checkoutId, discountId: discountCartItem.name, cartIds },
      {
        onSuccess: () => {
          notifications.show({
            message: 'Discount removed',
            variant: 'success',
          });
        },
        onError: (e) => {
          notifications.show({
            message: getErrorMessage_v2(e, 'Error removing discount'),
            variant: 'error',
          });
        },
      }
    );
  }, [cartIds, checkoutId, discountCartItem.name, removeDiscount]);

  return (
    <Group gap="xs">
      <Badge
        type="neutral"
        rightSection={
          <ActionIcon variant="transparent" size={16} c="gray.8" onClick={handleRemoveDiscount}>
            <IconXTabler brand="tabler" />
          </ActionIcon>
        }
      >
        {discountCartItem.name}
      </Badge>
      <Text fz="xs" c="gray.6">
        Applied to {causeData?.name} items
      </Text>
    </Group>
  );
};

const DiscountBadgeList = ({
  discountItems,
  cartIds,
  checkoutId,
}: {
  discountItems: ConsumerCheckoutDiscount[];
  cartIds: string[];
  checkoutId: string;
}) => {
  if (discountItems.length === 0) return null;
  return (
    <Stack gap="xs">
      {discountItems.map((discountItem) => (
        <DiscountBadge
          key={discountItem.uuid}
          discountCartItem={discountItem}
          cartIds={cartIds}
          causeId={discountItem.cause_id}
          checkoutId={checkoutId}
        />
      ))}
    </Stack>
  );
};
const DiscountInput = ({ checkoutId, cartIds }: { checkoutId: string; cartIds: string[] }) => {
  const { mutate: addDiscountToCheckout, isPending: isAddingDiscount } = useAddDiscountToCheckout();

  const discountCode = useField({
    initialValue: '',
    validate: () => false,
  });

  const handleApplyDiscount = useCallback(() => {
    addDiscountToCheckout(
      {
        checkoutId,
        discountCode: discountCode.getValue(),
        cartIds,
      },
      {
        onSuccess: () => {
          notifications.show({
            message: 'Discount applied',
            variant: 'success',
          });
          discountCode.reset();
        },
        onError: (e) => {
          discountCode.setError(getErrorMessage_v2(e, 'Error applying discount'));
        },
      }
    );
  }, [addDiscountToCheckout, cartIds, checkoutId, discountCode]);

  return (
    <Group w="100%" align="flex-start">
      <TextInput
        flex={1}
        placeholder="Enter discount code"
        {...discountCode.getInputProps()}
        onChange={(e) => {
          discountCode.setValue(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
        }}
      />
      <Button
        size="md"
        variant="outline"
        disabled={discountCode.getValue().length === 0}
        onClick={handleApplyDiscount}
        loading={isAddingDiscount}
      >
        Apply
      </Button>
    </Group>
  );
};

const DiscountSection = ({ checkoutId }: { checkoutId: string }) => {
  const { data: checkoutResponse } = useShowConsumerCheckout({
    checkoutId: checkoutId,
  });
  const cartIds = useMemo(() => {
    return checkoutResponse?.checkout.carts.map(({ uuid }) => uuid) ?? [];
  }, [checkoutResponse]);

  const discountItems = useMemo(() => {
    return checkoutResponse?.checkout.discounts;
  }, [checkoutResponse]);

  if (!checkoutResponse?.checkout.payment_method_needed || !cartIds.length) return null;

  return (
    <Stack>
      <Title order={4}>Add Discount Code</Title>

      <DiscountInput checkoutId={checkoutId} cartIds={cartIds} />

      <DiscountBadgeList
        discountItems={discountItems ?? []}
        cartIds={cartIds}
        checkoutId={checkoutId}
      />
    </Stack>
  );
};

export { DiscountSection };
